import { useEffect, useRef, useState } from 'react';
import { createAppListing, uploadAppIcon, APP_LISTING_NAME_MAX, type AppListingInfo } from '../api/developers';

/**
 * 開発者ポータル「連携アプリケーション」の新規掲載申請モーダル。
 * 実装は PublishConfirmModal.tsx の作法を踏襲（Escape で閉じる・body スクロールロック等）。
 */
interface NewAppListingModalProps {
  open: boolean;
  onCancel: () => void;
  onCreated: (listing: AppListingInfo) => void;
}

export default function NewAppListingModal({ open, onCancel, onCreated }: NewAppListingModalProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [iconKey, setIconKey] = useState<string | undefined>(undefined);
  const [iconPreviewUrl, setIconPreviewUrl] = useState<string | undefined>(undefined);
  const objectUrlRef = useRef('');

  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconError, setIconError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // 開くたびにフォームを初期状態に戻す（モーダル自体はマウントされたままの想定）。
  useEffect(() => {
    if (!open) return;
    setName('');
    setUrl('');
    setIconKey(undefined);
    setIconPreviewUrl(undefined);
    setIconError('');
    setFormError('');
    setSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  // アンマウント時にプレビュー用の objectURL を解放する（Upload.tsx のサムネイル選択と同じ作法）。
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIconError('');
    setUploadingIcon(true);
    try {
      const key = await uploadAppIcon(file);
      setIconKey(key);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const preview = URL.createObjectURL(file);
      objectUrlRef.current = preview;
      setIconPreviewUrl(preview);
    } catch (err) {
      setIconError(String(err));
    } finally {
      setUploadingIcon(false);
    }
  };

  const submit = async () => {
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName || !trimmedUrl) {
      setFormError('表示名とURLを入力してください。');
      return;
    }
    if (!/^https:\/\//.test(trimmedUrl)) {
      setFormError('URLは https:// で始まる必要があります。');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const listing = await createAppListing({ name: trimmedName, url: trimmedUrl, iconKey });
      onCreated(listing);
    } catch (e) {
      setFormError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="new-app-listing-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="new-app-listing-title" className="modal__title">
            新しいアプリを申請する
          </h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">
            ×
          </button>
        </div>
        <p className="modal__description">
          ホームの「連携アプリケーション」への掲載を申請します。運営の審査を経て掲載されます。
        </p>

        <div className="field">
          <label>アプリアイコン（任意）</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span className={`app-icon app-icon--lg ${!iconPreviewUrl ? 'app-icon--tone1' : ''}`}>
              {iconPreviewUrl ? <img src={iconPreviewUrl} alt="" /> : (name || '?').charAt(0).toUpperCase()}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="btn" style={{ alignSelf: 'flex-start' }}>
                画像を選択
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  disabled={uploadingIcon}
                  onChange={handleIconChange}
                />
              </label>
              {uploadingIcon && <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>アップロード中…</span>}
              {iconError && <span className="viewer__error">{iconError}</span>}
            </div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="newAppListingName">
            表示名 ({name.length}/{APP_LISTING_NAME_MAX})
          </label>
          <input
            id="newAppListingName"
            value={name}
            maxLength={APP_LISTING_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="newAppListingUrl">アプリの URL</label>
          <input
            id="newAppListingUrl"
            value={url}
            placeholder="https://example.com"
            onChange={(e) => setUrl(e.target.value)}
          />
          <p style={{ opacity: 0.8, fontSize: 13 }}>
            カードから移動する外部サイトの URL。https:// で始まる必要があります。
          </p>
        </div>

        {formError && <div className="viewer__error">{formError}</div>}

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={submitting || uploadingIcon}
            ref={confirmBtnRef}
          >
            {submitting ? '送信中…' : '掲載を申請する'}
          </button>
        </div>
      </div>
    </div>
  );
}
